// /src/Components/Clients/AdminPayoutSection.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
} from "react";
import { Toaster, toast } from "react-hot-toast";
import { Building2, Filter, RefreshCcw, CreditCard, Wallet } from "lucide-react";
import { GlobalContext } from "../../Context/GlobalContext";

import useTransactions from "../Services/PaymentServices/useTransactions";
/**
 * Payouts + Payment Logs + Settlement Config
 * - Auth fix: all API calls use `api` from GlobalContext (with interceptors)
 * - UI: Tailwind, green headers (#01C37D), rounded cards, badges
 * - Filters: date range, status, search
 * - Payouts: classic pagination (100/pg)
 * - Payments: infinite scroll (50/pg)
 * - Config: select client, view current config, update cycle/day
 */
export default function AdminPayoutSection({ Admin }) {
  const { getAllPayoutList, getAdminTransactionsReport, getSettlementConfig, updateSettlementConfigurations}=useTransactions();

  const AdminParse = typeof Admin === "string" ? JSON.parse(Admin) : Admin;
  const { api ,AllClients } = useContext(GlobalContext); 

  /* -------------------- Dates & Filters -------------------- */
  const today = () => new Date().toISOString().split("T")[0];
  const daysAgo = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().split("T")[0];
  };

  const [tab, setTab] = useState("payout"); // payout | payment | config
  const [startDate, setStartDate] = useState(daysAgo(7));
  const [endDate, setEndDate] = useState(today());
  const [status, setStatus] = useState(""); // "", "Y", "N", "P", "1", "0"
  const [search, setSearch] = useState("");
  const [applying, setApplying] = useState(false);

  /* -------------------- Geo -------------------- */
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLatitude(pos.coords.latitude);
        setLongitude(pos.coords.longitude);
      },
      (err) => console.warn("geolocation error:", err)
    );
  }, []);

  /* -------------------- Payouts (pagination) -------------------- */
  const [payoutRows, setPayoutRows] = useState([]);
  const [loadingPayouts, setLoadingPayouts] = useState(false);
  const [page, setPage] = useState(1);
  const PER_PAGE = 100;

  const fetchPayouts = async () => {
    setLoadingPayouts(true);

    try {
      const result =
        await getAllPayoutList({
          adminId: AdminParse?.pkid,
          userid: "",
          fromDate: startDate,
          toDate: endDate,
        });

      if (result.success) {
        setPayoutRows(result.data);
      } else {
        setPayoutRows([]);
        toast.error(result.message);
      }
    } catch (e) {
      setPayoutRows([]);
      toast.error("Error fetching payouts");
    } finally {
      setLoadingPayouts(false);
      setApplying(false);
    }
  };

  /* -------------------- Payments (infinite scroll) -------------------- */
  const [paymentRows, setPaymentRows] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsFirstLoading, setPaymentsFirstLoading] = useState(false);
  const [paymentsHasMore, setPaymentsHasMore] = useState(true);

  const paymentsScrollRef = useRef(null);
  const paymentsPageRef = useRef(1);
  const paymentsPerPageRef = useRef(50);
  const paymentsLockRef = useRef(false);
  const paymentsKeyRef = useRef("");

 const fetchPaymentsPage = async ({
  first = false,
  key,
}) => {
  if (
    paymentsLockRef.current ||
    !paymentsHasMore
  )
    return;

  paymentsLockRef.current = true;
  setPaymentsLoading(true);

  try {
    const result =
      await getAdminTransactionsReport({
        adnminId: String(
          AdminParse?.pkid
        ), // keep spelling
        userid: "",
        fromDate: String(startDate),
        toDate: String(endDate),
        pageIndex: String(
          paymentsPageRef.current
        ),
        recordPerPage: String(
          paymentsPerPageRef.current
        ),
      });

    if (result.success) {
      const list = result.data;

      if (
        key &&
        key !== paymentsKeyRef.current
      )
        return; // filters changed mid-flight

      setPaymentRows((prev) => {
        const idOf = (x) =>
          x.id ||
          x.trx_id ||
          x.transactionId ||
          x.txn_id ||
          x.refrence ||
          x.reference ||
          x.rrn ||
          `${x.datetime}|${x.reference}`;

        const seen = new Set(
          prev.map((x) => idOf(x))
        );

        const fresh = list.filter(
          (x) => !seen.has(idOf(x))
        );

        return prev.concat(fresh);
      });

      if (
        list.length <
        paymentsPerPageRef.current
      ) {
        setPaymentsHasMore(false);
      } else {
        paymentsPageRef.current += 1;
      }
    } else {
      if (first) setPaymentRows([]);
      setPaymentsHasMore(false);
      toast.error(result.message);
    }
  } catch (e) {
    if (first) setPaymentRows([]);
    setPaymentsHasMore(false);
    toast.error("Error fetching payments");
  } finally {
    setPaymentsLoading(false);
    paymentsLockRef.current = false;
    setApplying(false);
  }
};

  const resetAndLoadPayments = useCallback(async () => {
    const key = `${AdminParse?.pkid}|${startDate}|${endDate}`;
    paymentsKeyRef.current = key;
    setPaymentRows([]);
    setPaymentsHasMore(true);
    paymentsPageRef.current = 1;
    if (paymentsScrollRef.current) paymentsScrollRef.current.scrollTop = 0;
    setPaymentsFirstLoading(true);
    await fetchPaymentsPage({ first: true, key });
    setPaymentsFirstLoading(false);
  }, [AdminParse?.pkid, startDate, endDate]);

  const onPaymentsScroll = useCallback(() => {
    const el = paymentsScrollRef.current;
    if (!el || paymentsLoading || !paymentsHasMore) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
    if (nearBottom) fetchPaymentsPage({ key: paymentsKeyRef.current });
  }, [paymentsLoading, paymentsHasMore]);

  useEffect(() => {
    const el = paymentsScrollRef.current;
    if (!el) return;
    const handler = () => onPaymentsScroll();
    el.addEventListener("scroll", handler);
    return () => el.removeEventListener("scroll", handler);
  }, [onPaymentsScroll]);

  /* -------------------- Initial loads -------------------- */
  useEffect(() => {
    fetchPayouts();
    resetAndLoadPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* -------------------- Apply filters -------------------- */
  const applyFilters = () => {
    setApplying(true);
    setPage(1);
    fetchPayouts();
    resetAndLoadPayments();
  };

  const resetFilters = () => {
    setStartDate(daysAgo(7));
    setEndDate(today());
    setStatus("");
    setSearch("");
    setPage(1);
    setTimeout(applyFilters, 0);
  };

  /* -------------------- Filter helpers -------------------- */
  const matchesFilters = (row) => {
    const matchesSearch = !search
      ? true
      : Object.values(row).some((v) =>
          String(v ?? "").toLowerCase().includes(search.toLowerCase())
        );
    const s = status;
    const matchesStatus =
      !s ||
      row.status === s ||
      row.type === s ||
      row.settlement_status === s;
    return matchesSearch && matchesStatus;
  };

  // payouts use pagination (slice)
  const payoutFiltered = useMemo(
    () => (payoutRows || []).filter(matchesFilters),
    [payoutRows, search, status]
  );
  const payoutPaged = useMemo(
    () => payoutFiltered.slice((page - 1) * PER_PAGE, page * PER_PAGE),
    [payoutFiltered, page]
  );

  // payments use full filtered (infinite scroll)
  const paymentFiltered = useMemo(
    () => (paymentRows || []).filter(matchesFilters),
    [paymentRows, search, status]
  );

  const totalPages = Math.max(1, Math.ceil(payoutFiltered.length / PER_PAGE));
  const setPageSafe = (p) => setPage(Math.min(totalPages, Math.max(1, p)));

  const money = (v) =>
    v == null || isNaN(Number(v)) ? "₹ 0.00" : `₹ ${Number(v).toFixed(2)}`;

  /* -------------------- Settlement Config -------------------- */
  const [clients, setClients] = useState([]);
  const [userID, setUserID] = useState("");
  const [settlementCycle, setSettlementCycle] = useState("daily");
  const [settlementDay, setSettlementDay] = useState("monday");
  const [settlementData, setSettlementData] = useState([]);
  const [cfgLoading, setCfgLoading] = useState(false);

    useEffect(() => {
      setClients(AllClients)
    }, [AllClients])



 const loadSettlement = async (uid) => {
  if (!uid) {
    setSettlementData([]);
    return;
  }

  setCfgLoading(true);

  try {
    const result =
      await getSettlementConfig(uid);

    if (result.success) {
      setSettlementData(result.data);
    } else {
      setSettlementData([]);
      toast.error(result.message);
    }
  } catch (e) {
    setSettlementData([]);
    toast.error("Error loading configuration");
  } finally {
    setCfgLoading(false);
  }
};

const updateSettlement = async () => {
  if (!userID)
    return toast.error("Select a client");

  setCfgLoading(true);

  try {
    const result =
      await updateSettlementConfigurations({
        userid: userID,
        adminId: AdminParse?.pkid,
        settlementCycle,
        settlementDay,
      });

    if (result.success) {
      toast.success(result.message);
    } else {
      toast.error(result.message);
    }

    // reload config in both cases
    loadSettlement(userID);
  } catch (e) {
    toast.error("Error updating configuration");
  } finally {
    setCfgLoading(false);
  }
};

  // useEffect(() => {
  //   loadClients();
   
  // }, []);
  useEffect(() => {
    loadSettlement(userID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID]);

  /* -------------------- UI -------------------- */
  const FilterBar = (
    <div className="rounded-2xl border bg-white p-3">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700">
        <Filter size={16} /> Filters
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-sm">
          <div className="mb-1 text-xs text-gray-500">Start Date</div>
          <input
            type="date"
            className="h-10 w-44 rounded-xl border px-3 text-sm outline-none"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </label>
        <label className="text-sm">
          <div className="mb-1 text-xs text-gray-500">End Date</div>
          <input
            type="date"
            className="h-10 w-44 rounded-xl border px-3 text-sm outline-none"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </label>
        <label className="text-sm">
          <div className="mb-1 text-xs text-gray-500">Status</div>
          <select
            className="h-10 w-44 rounded-xl border bg-white px-3 text-sm outline-none"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="Y">Settled</option>
            <option value="N">Unsettled</option>
            <option value="P">Pending</option>
            <option value="1">Credit</option>
            <option value="0">Debit</option>
          </select>
        </label>
        <label className="text-sm">
          <div className="mb-1 text-xs text-gray-500">Search</div>
          <input
            className="h-10 w-64 rounded-xl border px-3 text-sm outline-none"
            placeholder="Search any text…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>

        <div className="ml-auto flex gap-2">
          <button
            className="inline-flex h-10 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50"
            onClick={resetFilters}
            disabled={loadingPayouts || paymentsLoading || applying}
          >
            <RefreshCcw size={16} /> Reset
          </button>
          <button
            className="h-10 rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white hover:bg-[#01C37D]/90"
            onClick={applyFilters}
            disabled={loadingPayouts || paymentsLoading || applying}
          >
            {applying ? "Applying…" : "Apply"}
          </button>
        </div>
      </div>

      {(search || status || startDate !== daysAgo(7) || endDate !== today()) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {search && <Badge onClear={() => setSearch("")}>Search: {search}</Badge>}
          {status && (
            <Badge onClear={() => setStatus("")}>
              Status: {{ Y: "Settled", N: "Unsettled", P: "Pending", 1: "Credit", 0: "Debit" }[status] || status}
            </Badge>
          )}
          {(startDate || endDate) && (
            <Badge onClear={() => { setStartDate(daysAgo(7)); setEndDate(today()); }}>
              Range: {startDate} → {endDate}
            </Badge>
          )}
        </div>
      )}
    </div>
  );

  const Tabs = (
    <div className="mt-3">
      <div className="mb-3 flex gap-2 rounded-2xl border bg-white p-1">
        {[
          { key: "payout", label: "Payout Report", icon: Wallet },
          { key: "payment", label: "Payment Logs", icon: CreditCard },
          { key: "config", label: "Settlement Config", icon: Building2 },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm ${
              tab === key ? "bg-[#01C37D] text-white" : "hover:bg-gray-50"
            }`}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  /* -------------------- Sections -------------------- */
  const PayoutSection = (
    <div className="overflow-hidden rounded-2xl border bg-white">
      <div className="overflow-auto">
        {loadingPayouts ? (
          <div className="grid place-items-center py-10 text-sm text-gray-500">
            Loading payouts…
          </div>
        ) : payoutPaged.length === 0 ? (
          <div className="grid place-items-center py-10 text-sm text-gray-500">
            No payout records found.
          </div>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="bg-[#01C37D] text-white">
              <tr>
                <th className="px-4 py-2">#</th>
                <th className="px-4 py-2">Client ID</th>
                <th className="px-4 py-2">Account</th>
                <th className="px-4 py-2">Business</th>
                <th className="px-4 py-2">Client</th>
                {/* <th className="px-4 py-2">Before</th> */}
                {/* <th className="px-4 py-2">After</th> */}
                <th className="px-4 py-2">Amount</th>
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {payoutPaged.map((item, i) => (
                <tr key={item.id || `${item.us_code}-${i}`} className="border-t">
                  <td className="px-4 py-2">{(page - 1) * PER_PAGE + i + 1}</td>
                  <td className="px-4 py-2">{item.us_code || "N/A"}</td>
                  <td className="px-4 py-2">{item.account_no || "N/A"}</td>
                  <td className="px-4 py-2">{item.business_name || "N/A"}</td>
                  <td className="px-4 py-2">{item.client_name || "N/A"}</td>
                  {/* <td className="px-4 py-2">{money(item.before_bal)}</td> */}
                  {/* <td className="px-4 py-2">{money(item.after_bal)}</td> */}
                  <td className="px-4 py-2">{money(item.amount)}</td>
                  <td className="px-4 py-2">
                    {item.created_on ? new Date(item.created_on).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-2">
                    {item.status === "Y" ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-current" /> Completed
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-0.5 text-xs font-semibold text-rose-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-current" /> Not Completed
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {payoutFiltered.length > PER_PAGE && (
        <div className="flex items-center justify-center gap-2 p-3">
          <button
            className="h-9 rounded-xl border px-3 text-sm"
            onClick={() => setPageSafe(page - 1)}
            disabled={page <= 1}
          >
            Prev
          </button>
          <span className="text-sm text-gray-600">
            Page <b>{page}</b> of <b>{totalPages}</b>
          </span>
          <button
            className="h-9 rounded-xl border px-3 text-sm"
            onClick={() => setPageSafe(page + 1)}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );

  const PaymentSection = (
    <div
      ref={paymentsScrollRef}
      className="max-h-[70vh] overflow-y-auto rounded-2xl border bg-white"
    >
      <table className="min-w-full text-left text-sm">
        <thead className="sticky top-0 bg-[#01C37D] text-white">
          <tr>
            <th className="px-4 py-2">S/N</th>
            <th className="px-4 py-2">Date</th>
            <th className="px-4 py-2">Business</th>
            <th className="px-4 py-2">RRN</th>
            <th className="px-4 py-2">Order/Ref ID</th>
            <th className="px-4 py-2">Customer</th>
            <th className="px-4 py-2">Mode</th>
            <th className="px-4 py-2">Credit</th>
            <th className="px-4 py-2">Debit</th>
            <th className="px-4 py-2">Charges</th>
            <th className="px-4 py-2">Net Settlement</th>
            <th className="px-4 py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {paymentsFirstLoading && paymentFiltered.length === 0 && (
            <tr>
              <td colSpan={12} className="px-4 py-6 text-center text-gray-500">
                Loading first page…
              </td>
            </tr>
          )}

          {paymentFiltered.map((item, idx) => (
            <tr
              key={
                item.id ||
                item.trx_id ||
                item.transactionId ||
                item.refrence ||
                `${item.rrn}-${idx}`
              }
              className="border-t"
            >
              <td className="px-4 py-2">{idx + 1}</td>
              <td className="px-4 py-2">{item.datetime || item.date || "N/A"}</td>
              <td className="px-4 py-2">
                {item.organization_name || item.business_name || "N/A"}
              </td>
              <td className="px-4 py-2">{item.rrn || "N/A"}</td>
              <td className="px-4 py-2 font-semibold">
                {item.refrence || item.reference || "N/A"}
              </td>
              <td className="px-4 py-2">{item.customer_name || "N/A"}</td>
              <td className="px-4 py-2">{item.payment_mode || item.mode || "N/A"}</td>
              <td className="px-4 py-2">{money(item.credit)}</td>
              <td className="px-4 py-2">{money(item.debit)}</td>
              <td className="px-4 py-2">{money(item.charges)}</td>
              <td className="px-4 py-2">{money(item.settlement_amount)}</td>
              <td className="px-4 py-2">
                {(item.settlement_status || item.status) === "Y" ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-current" /> Settled
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-current" /> Unsettled
                  </span>
                )}
              </td>
            </tr>
          ))}

          {paymentsLoading && paymentFiltered.length > 0 && (
            <tr>
              <td colSpan={12} className="px-4 py-4 text-center text-gray-500">
                Loading more…
              </td>
            </tr>
          )}
          {!paymentsLoading && paymentFiltered.length > 0 && !paymentsHasMore && (
            <tr>
              <td colSpan={12} className="px-4 py-4 text-center text-gray-500">
                End of results.
              </td>
            </tr>
          )}
          {!paymentsFirstLoading && paymentFiltered.length === 0 && (
            <tr>
              <td colSpan={12} className="px-4 py-6 text-center text-gray-500">
                No payment logs found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const ConfigSection = (
    <div className="grid gap-3 lg:grid-cols-2">
      {/* Left: current config cards */}
      <div className="rounded-2xl border bg-white p-3">
        <div className="mb-2 text-sm font-semibold text-gray-600">
          Existing Configurations
        </div>
        {cfgLoading && (
          <div className="py-6 text-center text-sm text-gray-500">Loading…</div>
        )}
        {!cfgLoading && settlementData?.length === 0 && (
          <div className="py-6 text-center text-sm text-gray-500">
            No configuration found.
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          {settlementData?.map((item, idx) => (
            <div key={idx} className="rounded-xl border p-3">
              <div className="text-xs font-semibold text-gray-500">
                Settlement Cycle
              </div>
              <div className="mt-1 text-sm">
                <b>Cycle:</b> {item.settlementCycle}
              </div>
              <div className="text-sm">
                <b>Day:</b> {item.settlementDay}
              </div>
              <div className="mt-2 text-xs text-gray-500">
                Created: {item.createdOn || "—"}
              </div>
              <div className="text-xs text-gray-500">
                Updated: {item.updatedOn || "—"}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right: editor */}
      <div className="rounded-2xl border bg-white p-3">
        <div className="mb-2 text-sm font-semibold text-gray-600">
          Update Configuration
        </div>

        <label className="block text-sm">
          <div className="mb-1 text-xs text-gray-500">Select Client</div>
          <select
            className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none"
            value={userID}
            onChange={(e) => setUserID(e.target.value)}
          >
            <option value="">Select client</option>
            {clients.map((c, i) => (
              <option key={i} value={c.us_code}>
                {c.us_code} — {c.us_name}
              </option>
            ))}
          </select>
        </label>

        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <div className="mb-1 text-xs text-gray-500">Settlement Cycle</div>
            <select
              className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none"
              value={settlementCycle}
              onChange={(e) => setSettlementCycle(e.target.value)}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </label>

          <label className="block text-sm">
            <div className="mb-1 text-xs text-gray-500">Settlement Day</div>
            <select
              className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none"
              value={settlementDay}
              onChange={(e) => setSettlementDay(e.target.value)}
            >
              <option value="monday">Monday</option>
              <option value="tuesday">Tuesday</option>
              <option value="wednesday">Wednesday</option>
              <option value="thursday">Thursday</option>
              <option value="friday">Friday</option>
              <option value="saturday">Saturday</option>
              <option value="sunday">Sunday</option>
            </select>
          </label>
        </div>

        <div className="mt-3">
          <button
            className="h-10 rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white hover:bg-[#01C37D]/90"
            onClick={updateSettlement}
            disabled={cfgLoading || !userID}
          >
            {cfgLoading ? "Updating…" : "Update Configuration"}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-3 lg:p-5">
      <Toaster position="top-right" />

      {/* Page header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
            <Building2 size={18} />
          </div>
          <div>
            <h1 className="text-xl font-semibold">Payout & Payment Logs</h1>
            <p className="text-sm text-gray-500">
              Filter, review, and configure settlements
            </p>
          </div>
        </div>
        <button
          className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50"
          onClick={applyFilters}
        >
          <RefreshCcw size={16} /> Refresh
        </button>
      </div>

      {FilterBar}
      <div className="mt-3">{Tabs}</div>

      {tab === "payout" && PayoutSection}
      {tab === "payment" && PaymentSection}
      {tab === "config" && ConfigSection}
    </div>
  );
}

/* ---------- tiny inline badge ---------- */
function Badge({ children, onClear }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border bg-gray-50 px-3 py-1 text-xs">
      {children}
      <button
        className="rounded-full px-1.5 text-gray-500 hover:bg-gray-200"
        onClick={onClear}
      >
        ✕
      </button>
    </span>
  );
}
